import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { usePhishPass } from '../../context/PhishPassContext';
import { Shield, MailOpen, MousePointerClick, AlertTriangle, Users, Send, Loader2 } from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, tone = 'slate', testId }) => {
  const toneMap = {
    slate: 'text-slate-600 bg-slate-100',
    blue: 'text-blue-600 bg-blue-100',
    amber: 'text-amber-600 bg-amber-100',
    red: 'text-red-600 bg-red-100',
    emerald: 'text-emerald-600 bg-emerald-100',
    indigo: 'text-indigo-600 bg-indigo-100',
  };
  return (
    <Card data-testid={testId}>
      <CardContent className="p-5 flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${toneMap[tone]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-xs text-slate-500 font-medium uppercase tracking-wide">{label}</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
};

const PhishPassDashboard = () => {
  const { getDashboardStats } = usePhishPass();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const data = await getDashboardStats();
      setStats(data);
      setLoading(false);
    })();
  }, [getDashboardStats]);

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  if (!stats) {
    return <div className="text-slate-500 text-center py-16">No data yet — send your first campaign to see metrics.</div>;
  }

  return (
    <div className="space-y-6" data-testid="phishpass-dashboard">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Send} label="Total Campaigns" value={stats.total_campaigns} tone="blue" testId="stat-campaigns" />
        <StatCard icon={Users} label="Total Recipients" value={stats.total_sent} tone="indigo" testId="stat-recipients" />
        <StatCard icon={MailOpen} label="Open Rate" value={`${stats.open_rate}%`} tone="emerald" testId="stat-open-rate" />
        <StatCard icon={MousePointerClick} label="Click Rate" value={`${stats.click_rate}%`} tone="amber" testId="stat-click-rate" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="repeat-offenders-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <h3 className="font-semibold text-slate-900">Repeat Offenders</h3>
                <Badge variant="outline" className="text-xs">≥ {stats.high_risk_threshold} clicks</Badge>
              </div>
              <span className="text-sm text-slate-500">{stats.repeat_offenders_count} flagged</span>
            </div>
            {stats.repeat_offenders.length === 0 ? (
              <div className="text-sm text-slate-400 italic py-6 text-center">No high-risk employees yet. 👍</div>
            ) : (
              <div className="space-y-2">
                {stats.repeat_offenders.slice(0, 8).map((r, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 bg-red-50 rounded border border-red-100">
                    <div>
                      <div className="font-medium text-sm text-slate-900">{r.name || r.email}</div>
                      <div className="text-xs text-slate-500">{r.email}</div>
                    </div>
                    <Badge className="bg-red-500 text-white">{r.clicks} clicks</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="top-clickers-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-amber-500" />
              <h3 className="font-semibold text-slate-900">Top Clickers (all-time)</h3>
            </div>
            {stats.top_clickers.length === 0 ? (
              <div className="text-sm text-slate-400 italic py-6 text-center">No clicks recorded yet.</div>
            ) : (
              <div className="space-y-2">
                {stats.top_clickers.map((r, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded">
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold">{i + 1}</div>
                      <div>
                        <div className="font-medium text-sm text-slate-900">{r.name || r.email}</div>
                        <div className="text-xs text-slate-500">{r.email}</div>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-amber-700 border-amber-300">{r.clicks} clicks</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PhishPassDashboard;
